import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

ChartJS.register(...registerables);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable22 = ({ data }) => {
  const pdfRef = useRef();

  // numeric conversion utility
  const numericData = data.map((d) => ({
    ...d,
    employment: Number(d.employment),
  }));

  // calculations
  const years = numericData.map((d) => d.year);
  const employmentValues = numericData.map((d) => d.employment);

  const overallAverage =
    employmentValues.reduce((s, v) => s + v, 0) / employmentValues.length;

  const max = numericData.reduce((a, b) =>
    a.employment > b.employment ? a : b,
  );
  const min = numericData.reduce((a, b) =>
    a.employment < b.employment ? a : b,
  );

  const chartData = {
    labels: [...years, "Avg"],
    datasets: [
      {
        label: "Employment",
        data: [...employmentValues, Number(overallAverage.toFixed(2))],
        backgroundColor: [
          ...employmentValues.map(() => "#B4530950"),
          "rgba(255, 99, 132, 0.9)", // Highlight average bar
        ],
        borderColor: [
          ...employmentValues.map(() => "#B45309"),
          "rgba(255, 99, 132, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "📊 American Indian & Alaska Native Employment (2018-2024)",
        font: { size: 20, weight: "bold" },
      },
    },
    scales: {
      y: {
        title: { display: true, text: "Employment" },
        beginAtZero: true,
      },
      x: {
        title: { display: true, text: "Year" },
      },
    },
  };

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text(APP_VERSION, 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("EngTable22_Employment.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-xl">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          📍 American Indian & Alaska Native Employment (2018-2024)
        </h2>

        {/* ✔ Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-green-100 rounded-lg shadow-lg text-center">
            <p className="text-sm text-gray-600">📈 Highest Recorded</p>
            <p className="text-lg font-semibold text-green-700">
              {max.employment} (Year: {max.year})
            </p>
          </div>

          <div className="p-4 bg-red-100 rounded-lg shadow-lg text-center">
            <p className="text-sm text-gray-600">📉 Lowest Recorded</p>
            <p className="text-lg font-semibold text-red-700">
              {min.employment} (Year: {min.year})
            </p>
          </div>

          <div className="p-4 bg-blue-100 rounded-lg shadow-lg text-center">
            <p className="text-sm text-gray-600">🧮 Average Employment</p>
            <p className="text-lg font-semibold text-blue-700">
              {overallAverage.toFixed(2)}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-center shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    <th className="p-2">Employment</th>
                  </tr>
                </thead>
                <tbody>
                  {numericData.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100 transition">
                      <td className="p-2 font-semibold">{row.year}</td>
                      <td className="p-2">
                        {Number(row.employment).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-bold">
                    <td className="p-2">Average</td>
                    <td className="p-2">{overallAverage.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable22;

